import { useState } from 'react'
import { motion } from 'framer-motion'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'
import {
    HelpCircle, MessageCircle, Phone, Mail, FileText, ChevronDown,
    ChevronUp, ExternalLink, Send, Search, BookOpen, Video, Headphones
} from 'lucide-react'

interface FAQ {
    question: string
    answer: string
    category: string
}

const faqs: FAQ[] = [
    {
        question: 'How do I access study materials?',
        answer: 'You can access all study materials from the "Study Material" section in your dashboard. Materials are organized by subject and chapter. Click on any material to view or download it.',
        category: 'Study'
    },
    {
        question: 'How are mock test scores calculated?',
        answer: 'Mock test scores follow the NEET marking scheme: +4 for correct answers and -1 for incorrect answers. Unanswered questions receive 0 marks. The total score is calculated as (Correct × 4) - (Incorrect × 1).',
        category: 'Tests'
    },
    {
        question: 'Can I reschedule a mock test?',
        answer: 'Weekly mock tests are scheduled for all students at a fixed time and cannot be rescheduled individually. However, practice tests can be taken at any time. Contact your mentor if you have a genuine emergency.',
        category: 'Tests'
    },
    {
        question: 'How do I contact my mentor?',
        answer: 'You can reach your mentor through the Chat section in your dashboard. Mentors are typically available from 9 AM to 6 PM on weekdays. For urgent matters, you can also call the support helpline.',
        category: 'Support'
    },
    {
        question: 'How do I make a payment?',
        answer: 'Go to the Payments section in your dashboard. Click "Pay Now" on your upcoming payment to proceed. We accept all major credit/debit cards, UPI, and net banking.',
        category: 'Payment'
    },
    {
        question: 'What happens if my payment fails?',
        answer: 'If your payment fails, please wait for 30 minutes and try again. The amount will be refunded within 3-5 business days if deducted. Contact support if issues persist.',
        category: 'Payment'
    },
]

const supportChannels = [
    { icon: MessageCircle, label: 'Live Chat', description: 'Chat with our support team', available: true, action: 'Start Chat' },
    { icon: Phone, label: 'Call Us', description: '+91 1800-XXX-XXXX', available: true, action: 'Call Now' },
    { icon: Mail, label: 'Email', description: 'support@futurefounder.com', available: true, action: 'Send Email' },
]

const quickLinks = [
    { icon: BookOpen, label: 'User Guide', description: 'Learn how to use the platform' },
    { icon: Video, label: 'Video Tutorials', description: 'Watch step-by-step guides' },
    { icon: FileText, label: 'Terms & Conditions', description: 'Read our terms of service' },
]

export function SupportView() {
    const [expandedFaq, setExpandedFaq] = useState<number | null>(null)
    const [searchQuery, setSearchQuery] = useState('')
    const [selectedCategory, setSelectedCategory] = useState<string>('all')
    const [ticketSubject, setTicketSubject] = useState('')
    const [ticketMessage, setTicketMessage] = useState('')
    const [showTicketForm, setShowTicketForm] = useState(false)

    const categories = ['all', 'Study', 'Tests', 'Payment', 'Support']

    const filteredFaqs = faqs.filter(faq => {
        const matchesSearch = faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
            faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
        const matchesCategory = selectedCategory === 'all' || faq.category === selectedCategory
        return matchesSearch && matchesCategory
    })

    const handleSubmitTicket = () => {
        console.log('Submitting ticket:', { ticketSubject, ticketMessage })
        setTicketSubject('')
        setTicketMessage('')
        setShowTicketForm(false)
    }

    return (
        <div className="space-y-4 md:space-y-6 pb-24 lg:pb-0">
            {/* Header */}
            <div className="text-center mb-6">
                <h2 className="text-xl md:text-2xl font-bold text-theme-primary mb-2">How can we help you?</h2>
                <p className="text-theme-secondary">Find answers to common questions or reach out to our support team</p>
            </div>

            {/* Search */}
            <div className="relative max-w-xl mx-auto">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <input
                    type="text"
                    placeholder="Search for help..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-theme-primary focus:ring-2 focus:ring-primary-blue/20 focus:border-primary-blue transition-all"
                />
            </div>

            {/* Support Channels */}
            <div className="grid md:grid-cols-3 gap-3">
                {supportChannels.map((channel, i) => (
                    <motion.div
                        key={channel.label}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.1 }}
                    >
                        <GlassmorphicCard className="p-4 text-center hover:shadow-lg transition-shadow cursor-pointer group">
                            <div className="w-12 h-12 rounded-xl bg-primary-blue/10 flex items-center justify-center mx-auto mb-3 group-hover:bg-primary-blue transition-colors">
                                <channel.icon className="h-6 w-6 text-primary-blue group-hover:text-white transition-colors" />
                            </div>
                            <h3 className="font-semibold text-theme-primary mb-1">{channel.label}</h3>
                            <p className="text-xs text-theme-secondary mb-3">{channel.description}</p>
                            <button className="text-sm text-primary-blue font-medium hover:underline">
                                {channel.action} →
                            </button>
                        </GlassmorphicCard>
                    </motion.div>
                ))}
            </div>

            {/* FAQs */}
            <GlassmorphicCard className="p-4 md:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                    <h3 className="font-bold text-theme-primary flex items-center gap-2">
                        <HelpCircle className="h-5 w-5 text-primary-blue" />
                        Frequently Asked Questions
                    </h3>
                    <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-lg overflow-x-auto">
                        {categories.map((cat) => (
                            <button
                                key={cat}
                                onClick={() => setSelectedCategory(cat)}
                                className={`px-3 py-1 text-xs font-medium rounded-md capitalize transition-all whitespace-nowrap ${selectedCategory === cat
                                        ? 'bg-white dark:bg-slate-700 shadow text-primary-blue'
                                        : 'text-slate-500 hover:text-slate-700'
                                    }`}
                            >
                                {cat}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="space-y-2">
                    {filteredFaqs.length === 0 ? (
                        <p className="text-center text-theme-secondary py-8">No FAQs match your search</p>
                    ) : (
                        filteredFaqs.map((faq, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: i * 0.05 }}
                                className="border border-slate-100 dark:border-slate-800 rounded-xl overflow-hidden"
                            >
                                <button
                                    onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
                                    className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                                >
                                    <span className="font-medium text-theme-primary text-sm">{faq.question}</span>
                                    {expandedFaq === i ? (
                                        <ChevronUp className="h-5 w-5 text-slate-400 shrink-0" />
                                    ) : (
                                        <ChevronDown className="h-5 w-5 text-slate-400 shrink-0" />
                                    )}
                                </button>
                                {expandedFaq === i && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        className="px-4 pb-4"
                                    >
                                        <p className="text-sm text-theme-secondary leading-relaxed">{faq.answer}</p>
                                    </motion.div>
                                )}
                            </motion.div>
                        ))
                    )}
                </div>
            </GlassmorphicCard>

            {/* Submit a Ticket */}
            <GlassmorphicCard className="p-4 md:p-6">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-theme-primary flex items-center gap-2">
                        <Headphones className="h-5 w-5 text-primary-blue" />
                        Submit a Support Ticket
                    </h3>
                    <button
                        onClick={() => setShowTicketForm(!showTicketForm)}
                        className="text-sm text-primary-blue font-medium"
                    >
                        {showTicketForm ? 'Cancel' : 'New Ticket'}
                    </button>
                </div>

                {showTicketForm ? (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className="space-y-4"
                    >
                        <div>
                            <label className="block text-sm font-medium text-theme-primary mb-2">Subject</label>
                            <input
                                type="text"
                                value={ticketSubject}
                                onChange={(e) => setTicketSubject(e.target.value)}
                                placeholder="Brief description of your issue"
                                className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-theme-primary focus:ring-2 focus:ring-primary-blue/20 focus:border-primary-blue transition-all"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-theme-primary mb-2">Message</label>
                            <textarea
                                rows={4}
                                value={ticketMessage}
                                onChange={(e) => setTicketMessage(e.target.value)}
                                placeholder="Describe your issue in detail..."
                                className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-theme-primary resize-none focus:ring-2 focus:ring-primary-blue/20 focus:border-primary-blue transition-all"
                            />
                        </div>
                        <button
                            onClick={handleSubmitTicket}
                            disabled={!ticketSubject || !ticketMessage}
                            className="w-full py-3 bg-primary-blue text-white rounded-xl font-medium hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                        >
                            <Send className="h-4 w-4" />
                            Submit Ticket
                        </button>
                    </motion.div>
                ) : (
                    <p className="text-sm text-theme-secondary">
                        Can't find what you're looking for? Submit a support ticket and our team will get back to you within 24 hours.
                    </p>
                )}
            </GlassmorphicCard>

            {/* Quick Links */}
            <div className="grid md:grid-cols-3 gap-3">
                {quickLinks.map((link, i) => (
                    <GlassmorphicCard key={i} className="p-4 flex items-center gap-3 hover:shadow-lg transition-shadow cursor-pointer group">
                        <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center group-hover:bg-primary-blue transition-colors">
                            <link.icon className="h-5 w-5 text-slate-500 group-hover:text-white transition-colors" />
                        </div>
                        <div className="flex-1">
                            <p className="font-medium text-theme-primary text-sm">{link.label}</p>
                            <p className="text-xs text-theme-secondary">{link.description}</p>
                        </div>
                        <ExternalLink className="h-4 w-4 text-slate-400" />
                    </GlassmorphicCard>
                ))}
            </div>
        </div>
    )
}
